import React from 'react'
import { Loader2 } from 'lucide-react'
import NextLink from 'next/link'
import { Button } from '../button'
import { cn } from '@/utilities/ui'

export interface FormButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'disabled' | 'onClick' | 'className'
> {
  children: React.ReactNode
  asChild?: boolean
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'outline' | 'primary-with-icon'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  width?: 'auto' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  className?: string
  borderRadius?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  'aria-label'?: string
}

export function FormButton({
  children,
  asChild = false,
  type = 'button',
  variant = 'primary',
  size = 'lg',
  width = 'auto',
  disabled = false,
  loading = false,
  onClick,
  className = '',
  borderRadius = 'full',
  'aria-label': ariaLabel,
  ...buttonProps
}: Readonly<FormButtonProps>) {
  const baseClasses = 'cursor-pointer transition-colors text-sm md:text-base'

  const variantClasses = {
    primary: cn(
      'border-none',
      'bg-tenant-primary-action-color',
      'text-tenant-primary-action-foreground',
      'hover:bg-tenant-primary-action-hover-color',
      'hover:text-tenant-primary-action-hover-foreground',
      'active:bg-tenant-primary-action-pressed-color',
      'active:text-tenant-primary-action-pressed-foreground',
      'disabled:text-tenant-button-disabled-text!',
      'disabled:bg-tenant-border',
    ),
    secondary: cn(
      'border',
      'border-light-grey',
      'bg-transparent',
      'text-tenant-primary-action-hover-color',
      'hover:bg-tenant-card-background',
      'active:bg-tenant-primary-action-pressed-color',
      'active:text-tenant-primary-action-pressed-foreground',
      'disabled:text-tenant-button-disabled-text!',
      'disabled:bg-tenant-border!',
    ),
    outline: cn(
      'border-2',
      'border-tenant-primary-action-color',
      'bg-tenant-card-background',
      'text-tenant-primary-action-hover-color',
      'hover:bg-tenant-progress-bg',
      'active:text-tenant-primary-action-pressed-foreground',
      'active:bg-tenant-primary-action-pressed-color',
      'active:border-tenant-primary-action-pressed-color',
      'disabled:text-tenant-button-disabled-text!',
      'disabled:bg-tenant-border',
      'disabled:border-tenant-border',
      'disabled:cursor-not-allowed',
    ),
    'primary-with-icon': cn(
      'border-none',
      'bg-tenant-primary-action-color',
      'text-tenant-primary-action-foreground!',
      'hover:bg-tenant-primary-action-hover-color',
      'active:bg-tenant-primary-action-pressed-color',
      'active:text-tenant-primary-action-pressed-foreground!',
      'relative',
      'flex',
      'items-center',
      'justify-between',
      'gap-6',
      'pl-6',
      'pr-2',
      '*:text-tenant-primary-action-foreground!',
      'active:*:text-tenant-primary-action-pressed-foreground!',
      'disabled:*:text-tenant-button-disabled-text!',
      'disabled:bg-tenant-border',
      'disabled:border-light-grey',
      'disabled:cursor-wait',
      'disabled:pointer-events-auto',
    ),
  }

  const sizeClasses = {
    sm: 'h-10 px-3',
    md: 'h-12 px-4',
    lg: 'h-14 px-4',
    xl: 'h-16 px-6',
  }

  // Height-only sizes so primary-with-icon can keep asymmetric pl-6 / pr-2 padding.
  const primaryWithIconSizeClasses = {
    sm: 'h-10',
    md: 'h-12',
    lg: 'h-14',
    xl: 'h-16',
  }

  const widthClasses = {
    auto: 'w-auto',
    sm: 'w-[120px]',
    md: 'w-[150px] md:w-[180px]',
    lg: 'w-[280px] md:w-[320px]',
    xl: 'w-[320px] md:w-[400px]',
    full: 'w-full',
  }

  const radiusClasses = {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '20px',
    full: '40px',
  }

  const childrenContent = loading ? <span>Loading...</span> : children
  // Special handling for primary-with-icon variant
  if (variant === 'primary-with-icon') {
    const primaryWithIconClassName = cn(
      baseClasses,
      variantClasses[variant],
      primaryWithIconSizeClasses[size],
      widthClasses[width],
      disabled && 'bg-tenant-border cursor-not-allowed',
      loading && 'cursor-not-allowed',
      className,
    )
    const loadingContent = loading ? (
      <Loader2 className="w-5 h-5 animate-spin text-tenant-primary-action-color" />
    ) : (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M5 12H19M19 12L12 5M19 12L12 19"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-tenant-primary-action-color"
        />
      </svg>
    )
    return (
      <Button
        asChild={asChild}
        {...buttonProps}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={primaryWithIconClassName}
        style={{
          borderRadius: radiusClasses[borderRadius],
        }}
        aria-label={ariaLabel}
      >
        <span className="text-left">{childrenContent}</span>

        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0">
          {loadingContent}
        </div>
      </Button>
    )
  }

  const combinedClassName = cn(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClasses[width],
    disabled && 'bg-tenant-border cursor-not-allowed',
    loading && 'cursor-not-allowed',
    className,
  )

  return (
    <Button
      {...buttonProps}
      asChild={asChild}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={combinedClassName}
      style={{
        borderRadius: radiusClasses[borderRadius],
      }}
      aria-label={ariaLabel}
    >
      {childrenContent}
    </Button>
  )
}

/** Not supported on links: that variant renders multiple children inside Button, which is incompatible with `asChild` + Radix Slot. */
export type FormLinkButtonVariant = Exclude<
  NonNullable<FormButtonProps['variant']>,
  'primary-with-icon'
>

export interface FormLinkButtonProps extends Omit<
  FormButtonProps,
  'asChild' | 'type' | 'onClick' | 'variant' | 'loading' | 'disabled'
> {
  href: React.ComponentProps<typeof NextLink>['href']
  prefetch?: React.ComponentProps<typeof NextLink>['prefetch']
  replace?: React.ComponentProps<typeof NextLink>['replace']
  scroll?: React.ComponentProps<typeof NextLink>['scroll']
  target?: string
  onClick?: () => void
  variant?: FormLinkButtonVariant
}

// Per-variant important text overrides so text wins over `.links-inherit-color a`
// in containers like headers that apply that utility to all descendant anchors.
const LINK_COLOR_OVERRIDES: Record<FormLinkButtonVariant, string> = {
  primary: cn(
    'text-tenant-primary-action-foreground!',
    'hover:text-tenant-primary-action-hover-foreground!',
    'active:text-tenant-primary-action-pressed-foreground!',
  ),
  secondary: cn(
    'text-tenant-primary-action-hover-color!',
    'active:text-tenant-primary-action-pressed-foreground!',
  ),
  outline: cn(
    'text-tenant-primary-action-hover-color!',
    'active:text-tenant-primary-action-pressed-foreground!',
  ),
}

export function FormLinkButton({
  href,
  prefetch,
  replace,
  scroll,
  target,
  children,
  variant = 'primary',
  onClick,
  ...rest
}: Readonly<FormLinkButtonProps>) {
  return (
    <FormButton asChild variant={variant} {...rest}>
      <NextLink
        href={href}
        prefetch={prefetch}
        replace={replace}
        scroll={scroll}
        target={target}
        onClick={onClick}
        className={LINK_COLOR_OVERRIDES[variant]}
      >
        {children}
      </NextLink>
    </FormButton>
  )
}
